/**
 * Утилита для логирования событий аутентификации на бэкенд
 * 
 * Логи отправляются на эндпоинт /api/auth/log и записываются в /logs/auth.log
 * с маркировкой [Frontend]
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';

export type AuthEventType = 'login' | 'register' | 'password_change' | 'logout';

export interface AuthLogEvent {
  event_type: AuthEventType;
  email: string;
  success: boolean;
  error_message?: string | null;
  ip_address?: string | null;
}

/**
 * Отправить событие аутентификации на логирование
 * 
 * @param event - Данные события для логирования
 * 
 * @example
 * // Логирование успешного входа
 * await logAuthEvent({
 *   event_type: 'login',
 *   email: 'user@example.com',
 *   success: true
 * });
 * 
 * @example
 * // Логирование ошибки регистрации
 * await logAuthEvent({
 *   event_type: 'register',
 *   email: 'user@example.com',
 *   success: false,
 *   error_message: 'Email already registered'
 * });
 */
export async function logAuthEvent(event: AuthLogEvent): Promise<void> {
  try {
    await fetch(`${API_URL}/auth/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    });
    // Игнорируем ошибки логирования - они не должны влиять на работу приложения
  } catch (error) {
    // Тихо игнорируем ошибки логирования в консоль
    console.warn('[AuthLogger] Failed to send log event:', error);
  }
}

/**
 * Helper функции для конкретных событий
 */

export const logLoginSuccess = (email: string) =>
  logAuthEvent({ event_type: 'login', email, success: true });

export const logLoginFailure = (email: string, errorMessage?: string) =>
  logAuthEvent({ event_type: 'login', email, success: false, error_message: errorMessage });

export const logRegisterSuccess = (email: string) =>
  logAuthEvent({ event_type: 'register', email, success: true });

export const logRegisterFailure = (email: string, errorMessage?: string) =>
  logAuthEvent({ event_type: 'register', email, success: false, error_message: errorMessage });

export const logPasswordChangeSuccess = (email: string) =>
  logAuthEvent({ event_type: 'password_change', email, success: true });

export const logPasswordChangeFailure = (email: string, errorMessage?: string) =>
  logAuthEvent({ event_type: 'password_change', email, success: false, error_message: errorMessage });

export const logLogout = (email: string) =>
  logAuthEvent({ event_type: 'logout', email, success: true });
